// The reel, on one page: a phone-shaped stage on the left that always shows the reel as it is
// right now, and one column on the right that holds only the thing to do next. The step is read
// off the server (the post, its storyboard jobs, its current version), so leaving and coming
// back lands where the reel actually is. Nothing typed on one step is asked for again.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import type { GenerationDto, PostDto, VersionDto } from '@str/api/types';
import type { ReelChoices, TextLayer } from '@str/shared';
import {
  BRIEF_MAX,
  CHANNEL_META,
  CREDIT_COST,
  joinCaption,
  propertyChannels,
  reelQuote,
  splitCaption,
  type Channel,
  type PostTemplate,
  type ReelJobDto,
  type ReelRequest,
} from '@str/shared';
import { useCreditsSheet } from '@/components/CreditsSheetHost';
import { creditShortfall, errorMessage, unwrap } from '@/lib/api';
import { dayLabel, toIsoDate } from '@/lib/format';
import { startPoll, type PollHandle } from '@/lib/poll';
import { useActiveProperty } from '@/lib/property-store';
import { publishFailure } from '@/lib/publish-status';
import { keys, postsApi, useChannels, useMe, useMedia, usePost, usePosts } from '@/lib/queries';
import {
  createReelPost,
  generateReelMusic,
  reelDraft,
  reelSource,
  reelTracks,
  renderReel,
  startReelJob,
} from '@/lib/reel';
import { Moment } from '@/components/composer2/Moment';
import {
  MOODS,
  SONG_LETTERS,
  WORD_SIZES,
  filmEstimate,
  filmLede,
  lineAt,
  sceneLines,
  sceneTimings,
  wordsLayers,
  boardOf,
  creditLine,
  MUSIC_PRICE,
  MUSIC_PRICE_LINE,
  defaultMood,
  defaultWords,
  filmSteps,
  keepFilmed,
  keepSilent,
  keepWords,
  keptSilent,
  keptWords,
  loadReelDraft,
  phaseOf,
  shotsLeft,
  railFor,
  revisionQuote,
  saveReelDraft,
  sceneList,
  storyboardSteps,
  titleWords,
  type FlowStep,
  type Mood,
  type Phase,
} from '@/lib/reel-flow';
import { PICTURE_FONTS } from '@/lib/picture-render';
import { hostTimeZone } from '@/lib/schedule';
import { loadWizard, saveWizard, type Following } from '@/lib/wizard';
import { ScheduleSheet } from './ScheduleSheet';
import '@/components/composer2/composer2.css';
import { FlowHeader } from '@/components/composer2/FlowHeader';
import { defaultReelSound, ORIGINAL_UNDER_MUSIC } from '@/lib/reel-sound';
import './reel.css';

const api = <T,>(url: string, body?: unknown) =>
  unwrap<T>(
    fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json' } : {},
      credentials: 'include',
      ...(body ? { body: JSON.stringify(body) } : {}),
    }),
  );

type Song = { id: string; moodId: Mood; title: string; url: string; seconds: number };

/** Play without ever throwing: a browser may refuse until the host taps something. */
function tryPlay(el: HTMLMediaElement | null) {
  try {
    void Promise.resolve(el?.play()).catch(() => {});
  } catch {
    /* not playable here */
  }
}
type NavState = { fresh?: string; line?: string; follow?: boolean } | null;

export function Reel() {
  const { active } = useActiveProperty();
  const location = useLocation();
  const fresh = (location.state as NavState)?.fresh ?? '';
  return <ReelBody key={`${active.id}:${fresh}`} />;
}

function ReelBody() {
  const { active, registerDraft } = useActiveProperty();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const library = useMedia(active.id);
  const posts = usePosts(active.id);
  const connected = useChannels(active.id);
  const credits = useCreditsSheet();
  const navState = location.state as NavState;
  const fresh = !!navState?.fresh;

  // What Inspiration left for this reel, read once and cleared: the moment written from the
  // followed post's hint, its form, and whose it was. Without `fresh` nothing old is picked up.
  const [arrived] = useState(() => {
    if (!fresh) return null;
    // Only a page that left a reference (Inspiration, a post's results) says so; the wizard's
    // Post/Reel switch hands over its words alone, and never someone else's post.
    const w = navState?.follow ? loadWizard(active.id) : null;
    if (navState?.follow) saveWizard(active.id, null);
    return w;
  });
  const [kept] = useState(() => (fresh ? null : loadReelDraft(active.id)));
  const [postId, setPostId] = useState<string | null>(
    () => params.get('post') ?? (fresh ? null : kept?.postId) ?? null,
  );
  const [brief, setBrief] = useState(
    () => navState?.line ?? arrived?.line ?? (params.get('post') ? '' : (kept?.brief ?? '')),
  );
  const [seconds, setSeconds] = useState<15 | 30>(kept?.seconds ?? 15);
  const [fidelity, setFidelity] = useState<ReelRequest['fidelity']>(kept?.fidelity ?? 'original');
  const [template, setTemplate] = useState<PostTemplate | null>(arrived?.template ?? null);
  const [following, setFollowing] = useState<Following | null>(arrived?.following ?? null);
  const [editing, setEditing] = useState(false);
  const [revision, setRevision] = useState('');
  const [sceneIdx, setSceneIdx] = useState(0);
  /** What is open under the scene on the stage: its redo note, or the photo picker. */
  const [sceneAction, setSceneAction] = useState<'redo' | 'swap' | null>(null);
  const [sceneNote, setSceneNote] = useState('');
  /** The note field for redoing the whole board, behind its quiet link. */
  const [wholeBoard, setWholeBoard] = useState(false);
  /** The last scene dropped, so it can be put back for the rest of this visit. */
  const [undo, setUndo] = useState<{
    boardId: string;
    scene: number;
    scenes: { shotId: string }[];
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mixing, setMixing] = useState<{ id: string; what?: 'words' | 'music' } | null>(null);
  const [changingMusic, setChangingMusic] = useState(false);
  const [silent, setSilent] = useState(() => (postId ? keptSilent(postId) : false));
  // A reel kept silent on another device is silent here too, once the post has loaded.
  const [silentSeeded, setSilentSeeded] = useState<string | null>(null);
  // The words on the reel: the layer being placed, and whether the host has settled them.
  const [wordsLayer, setWordsLayer] = useState<TextLayer | null>(null);
  /** One line per scene in the layer's style, or null for the one line throughout. */
  const [lines, setLines] = useState<string[] | null>(null);
  /** The live preview's playhead and length, so the line on the phone follows the picture. */
  const [playMs, setPlayMs] = useState(0);
  const [videoMs, setVideoMs] = useState(0);
  const [wordsSettled, setWordsSettled] = useState(() =>
    postId ? keptWords(postId) !== null : false,
  );
  /** From share, the host may revisit the words or the music; the render finishes it again. */
  const [revisit, setRevisit] = useState<'words' | 'music' | null>(null);
  const [drag, setDrag] = useState<{ dx: number; dy: number } | null>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const [mood, setMood] = useState<Mood | null>(null);
  const [songs, setSongs] = useState<Song[]>([]);
  const [composing, setComposing] = useState<Mood | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  /** The finished reel on the share step: playing or not, so its Play button can say so. */
  const [reelPlaying, setReelPlaying] = useState(false);
  const [captionBody, setCaptionBody] = useState<string | null>(null);
  const [sheet, setSheet] = useState<'share' | null>(null);
  const [channels, setChannels] = useState<Channel[] | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hydrated = useRef<string | null>(null);
  const creating = useRef<Promise<string> | null>(null);
  const recovered = useRef<string | null>(null);
  const auditioned = useRef<string | null>(null);
  const mixPoll = useRef<PollHandle | null>(null);

  const postQ = usePost(postId);
  const post: PostDto | null = postQ.data ?? null;
  if (post && postId && silentSeeded !== postId) {
    setSilentSeeded(postId);
    if (post.reelChoices?.silent) setSilent(true);
  }
  const jobsQ = useQuery({
    queryKey: ['reel-jobs', postId],
    enabled: !!postId,
    queryFn: () => api<{ jobs: ReelJobDto[] }>(`/api/posts/${postId}/reels`).then((r) => r.jobs),
  });
  const jobs = jobsQ.data ?? [];
  const working = jobs.find((j) => j.state === 'pending' || j.state === 'running') ?? null;
  const board = boardOf(jobs);
  const latest = jobs.at(-1) ?? null;
  const version: VersionDto | null =
    post?.versions.find((v) => v.id === post.currentVersionId) ?? null;
  const basePhase = phaseOf({
    post,
    jobs,
    version: version ? { videoUrl: version.videoUrl, videoEdit: version.videoEdit } : null,
    mixing: !!mixing,
    silent,
    editing,
    words: wordsSettled,
  });
  const phase: Phase =
    revisit && basePhase === 'share' && !working && !mixing
      ? revisit
      : changingMusic && basePhase === 'share' && !working && !mixing
        ? 'music'
        : basePhase;
  const balance = me.data?.credits?.balance ?? null;
  const boardCost = reelQuote('storyboard', seconds).credits;
  const filmCost = reelQuote('video', board?.input.seconds ?? seconds).credits;
  const locked = busy || !!working;
  /** What the whole reel comes to, so the sheet can say a pack covers it. */
  const reelTotal = boardCost + reelQuote('video', board?.input.seconds ?? seconds).credits;

  /* ------------------------------------------------- the post that carries the reel */

  useEffect(() => {
    saveReelDraft(active.id, { brief, seconds, fidelity, postId });
  }, [active.id, brief, seconds, fidelity, postId]);

  useEffect(() => {
    if (!post) return;
    if (post.propertyId !== active.id) {
      // The draft belongs to another property: not this page's.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPostId(null);
      setParams({}, { replace: true });
      return;
    }
    // A reel this device remembered but the URL did not name: picked up only while it is still
    // being made. A finished one lives in Drafts; Create → Reel means a new reel.
    if (
      !params.get('post') &&
      hydrated.current !== post.id &&
      post.versions.some((v) => v.id === post.currentVersionId && v.videoUrl)
    ) {
      setPostId(null);
      setBrief('');
      saveReelDraft(active.id, null);
      return;
    }
    if (hydrated.current !== post.id) {
      hydrated.current = post.id;
      setBrief(post.brief);
    }
  }, [post, active.id, setParams, params]);

  const changed = !!post && brief !== post.brief;
  const save = useCallback(async () => {
    if (postId && post) {
      if (changed) {
        const p = await postsApi.update(postId, { brief });
        qc.setQueryData(keys.post(postId), p);
      }
      return postId;
    }
    if (creating.current) return creating.current;
    creating.current = createReelPost(active.id, {
      brief,
      channels: active.channels,
      template,
      inspirationPostId: following?.inspirationPostId ?? null,
    }).then((p) => {
      setPostId(p.id);
      hydrated.current = p.id;
      setParams({ post: p.id }, { replace: true, state: location.state as unknown });
      qc.setQueryData(keys.post(p.id), p);
      return p.id;
    });
    try {
      return await creating.current;
    } finally {
      creating.current = null;
    }
  }, [
    postId,
    post,
    changed,
    brief,
    active.id,
    active.channels,
    template,
    following,
    qc,
    setParams,
    location.state,
  ]);

  useEffect(
    () =>
      registerDraft(
        brief.trim() && !postId
          ? {
              save: async () => {
                await save();
              },
              discard: () => saveReelDraft(active.id, null),
              busy: locked,
            }
          : null,
      ),
    [registerDraft, brief, postId, save, locked, active.id],
  );

  /* ------------------------------------------------- following a job the service is running */

  const workingId = working?.id;
  useEffect(() => {
    if (!workingId || !postId) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const { job } = await api<{ job: ReelJobDto }>(`/api/posts/${postId}/reels/${workingId}`);
        if (stopped) return;
        qc.setQueryData<ReelJobDto[]>(['reel-jobs', postId], (old) =>
          (old ?? []).map((j) => (j.id === job.id ? job : j)),
        );
        if (job.state === 'complete' || job.state === 'failed') {
          await qc.invalidateQueries({ queryKey: keys.post(postId) });
          await qc.invalidateQueries({ queryKey: keys.posts(active.id) });
          await qc.invalidateQueries({ queryKey: keys.me });
          return;
        }
      } catch (e) {
        if (!stopped) setError(errorMessage(e));
      }
      if (!stopped) timer = setTimeout(() => void poll(), 2500);
    };
    void poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [workingId, postId, qc, active.id]);

  /* ------------------------------------------------- laying the track under the reel */

  const followMix = useCallback(
    (id: string, what: 'words' | 'music' = 'music') => {
      setMixing({ id, what });
      mixPoll.current?.cancel();
      mixPoll.current = startPoll<GenerationDto>(`reel-mix:${id}`, {
        first: 800,
        request: () => postsApi.poll(id),
        isDone: (g) => g.status !== 'running',
        onResult: (g) => {
          if (g.status === 'running') return;
          if (g.post) qc.setQueryData(keys.post(g.post.id), g.post);
          if (g.status === 'failed') setError(g.statusText || 'The reel could not be finished.');
          else {
            setChangingMusic(false);
            setSongs([]);
            setPicked(null);
            if (g.post) {
              keepWords(g.post.id, null);
              // Baked in now: the kept words have done their job.
              void postsApi.update(g.post.id, { reelChoices: null }).catch(() => {});
            }
          }
          setMixing(null);
          if (postId) void qc.invalidateQueries({ queryKey: keys.post(postId) });
        },
        onError: (e) => {
          setMixing(null);
          setError(errorMessage(e));
          return false;
        },
      });
    },
    [qc, postId],
  );
  useEffect(() => () => mixPoll.current?.cancel(), []);
  useEffect(() => {
    const r = post?.renderRecovery;
    if (r?.status === 'running' && recovered.current !== r.id) {
      recovered.current = r.id;
      followMix(r.id);
    }
  }, [post?.renderRecovery, followMix]);

  /* ------------------------------------------------- the words on the reel */

  // The starting words are settled during render, the React way: once per post, after the jobs
  // have loaded so the storyboard's title, not the post's, is the line.
  const [wordedFor, setWordedFor] = useState<string | null>(null);
  if ((phase === 'words' || phase === 'music') && postId && jobsQ.data && wordedFor !== postId) {
    setWordedFor(postId);
    // This device's copy first, then the post's: words kept elsewhere come back too.
    const kept = keptWords(postId) ?? post?.reelChoices?.words ?? null;
    const title = titleWords(
      board?.result?.story?.title ?? post?.title,
      splitCaption(post?.caption ?? '').body,
    );
    const sceneCount = board?.result?.shots?.length ?? 0;
    if (kept) {
      setWordsLayer(kept.layer);
      setLines(kept.lines ?? null);
      setWordsSettled(true);
    } else {
      // Words already baked into this version (revisiting from share) are the starting point.
      const bakedLayers =
        version?.videoEdit?.layers?.filter((l): l is TextLayer => l.kind === 'text') ?? [];
      const baked = bakedLayers[0];
      setWordsLayer(baked ?? defaultWords(title));
      // Several baked lines were one per scene; a fresh reel with scenes starts that way too.
      setLines(
        bakedLayers.length > 1
          ? bakedLayers.map((l) => l.text)
          : baked || sceneCount < 2
            ? null
            : sceneLines(board!.result!.shots!, title),
      );
    }
  }
  const sceneShots = board?.result?.shots ?? [];
  /** The film being made, or the one just finished while its version loads. */
  const filmJob =
    working?.kind === 'video' ? working : (jobs.filter((j) => j.kind === 'video').at(-1) ?? null);
  // The progress line names the shots filmed only between checks, and shots film side by side:
  // keep the highest count seen for this take so neither the ladder nor the lede counts down (J14).
  const filmedRef = useRef<{ id: string; filmed: number } | null>(null);
  filmedRef.current = keepFilmed(filmedRef.current, filmJob);
  const filmed = filmedRef.current?.filmed ?? 0;
  const readLeft = shotsLeft(filmJob, sceneShots.length);
  const filmLeft = Math.min(readLeft ?? sceneShots.length, Math.max(0, sceneShots.length - filmed));
  // A clock for the filming line only, so it can say when the take runs past its estimate.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (phase !== 'filming') return;
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [phase]);
  const plannedMs = sceneShots.reduce((sum, s) => sum + s.seconds, 0) * 1000;
  const lineTimings = sceneTimings(sceneShots, videoMs || plannedMs);
  /** The words on the phone right now: the scene's line under the playhead, or the one line. */
  const liveWords = wordsLayer
    ? lines
      ? (lines[lineAt(lineTimings, playMs)] ?? '')
      : wordsLayer.text
    : '';

  /** The host's choices ride on the post, so a reload or another device picks them up. */
  const saveChoices = (next: ReelChoices) => {
    if (!postId) return;
    const merged = { ...(post?.reelChoices ?? {}), ...next };
    void postsApi
      .update(postId, { reelChoices: merged })
      .then((p) => qc.setQueryData(keys.post(p.id), p))
      .catch(() => {});
  };
  const settleWords = (layer: TextLayer | null) => {
    if (!postId) return;
    const words = layer && lines ? { layer, lines } : { layer };
    keepWords(postId, words);
    saveChoices({ words });
    setWordsLayer(layer);
    setWordsSettled(true);
    if (revisit === 'words') setRevisit('music');
  };
  const reopenWords = () => {
    stopSound();
    if (postId) keepWords(postId, null);
    saveChoices({ words: null });
    setWordsSettled(false);
    if (basePhase === 'share') setRevisit('words');
  };
  const dragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (phase !== 'words' || !wordsLayer || !phoneRef.current) return;
    const r = phoneRef.current.getBoundingClientRect();
    setDrag({
      dx: (e.clientX - r.left) / r.width - wordsLayer.x,
      dy: (e.clientY - r.top) / r.height - wordsLayer.y,
    });
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const dragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag || !wordsLayer || !phoneRef.current) return;
    const r = phoneRef.current.getBoundingClientRect();
    const x = Math.min(1 - wordsLayer.w, Math.max(0, (e.clientX - r.left) / r.width - drag.dx));
    const y = Math.min(0.92, Math.max(0, (e.clientY - r.top) / r.height - drag.dy));
    setWordsLayer({ ...wordsLayer, x, y });
  };
  const dragEnd = () => setDrag(null);

  /* ------------------------------------------------- the music: a song per mood, more on request */

  /**
   * Every song is a credit. An empty wallet opens the sheet before anything is composed, and a
   * 402 the wallet check could not see coming opens it too; buying runs the song straight after
   * (`checked`: the retry skips the wallet it read before the pack landed).
   */
  const compose = useCallback(
    async (m: Mood, checked = false) => {
      if (!postId) return;
      const retry = () => void compose(m, true);
      if (!checked && balance !== null && balance < CREDIT_COST.musicTrack) {
        credits.open(CREDIT_COST.musicTrack, retry, 'compose a song');
        return;
      }
      setComposing(m);
      setError(null);
      try {
        const t = await generateReelMusic(postId, m);
        const song: Song = {
          id: t.id,
          moodId: m,
          title: t.label || 'Soundtrack',
          url: t.url ?? `/api/media/${t.id}/file`,
          seconds: t.durationSeconds ?? 0,
        };
        setSongs((cur) => [...cur, song]);
        // The new song is the one to hear: it plays over the reel as soon as it lands.
        setPicked(song.id);
        const audio = audioRef.current;
        if (audio) {
          audio.src = song.url;
          audio.currentTime = 0;
          if (videoRef.current) videoRef.current.currentTime = 0;
          // A phone may refuse sound nobody tapped for: then it reads Sound off, honestly.
          playSong(audio, song.id);
        }
      } catch (e) {
        const short = creditShortfall(e);
        if (short)
          credits.open(short.needed, retry, 'compose a song', undefined, {
            balance: short.balance,
            actions: short.actions,
          });
        else setError(errorMessage(e));
      } finally {
        setComposing(null);
      }
    },
    [postId, balance, credits],
  );
  const chosenMood: Mood = mood ?? defaultMood(post?.template ?? null);
  /** Playing is what the browser allowed, not what was asked for. */
  function playSong(audio: HTMLAudioElement, id: string) {
    setPlaying(null);
    try {
      void Promise.resolve(audio.play()).then(
        () => setPlaying(id),
        () => setPlaying(null),
      );
    } catch {
      /* not playable here */
    }
  }
  useEffect(() => {
    if (phase !== 'music' || !postId || auditioned.current === postId) return;
    auditioned.current = postId;
    // Every song made for this reel is remembered by mood; only a mood with none composes one.
    void reelTracks(active.id)
      .then((tracks) => {
        const mine = tracks
          .filter((t) => t.postId === postId && t.source === 'generated')
          .reverse()
          .map((t) => ({
            id: t.id,
            moodId: (t.moodId ?? chosenMood) as Mood,
            title: t.title,
            url: t.url,
            seconds: t.durationSeconds,
          }));
        setSongs(mine);
        if (!mine.some((t) => t.moodId === chosenMood)) return compose(chosenMood);
        setPicked(mine.find((t) => t.moodId === chosenMood)!.id);
      })
      .catch(() => compose(chosenMood));
  }, [phase, postId, active.id, compose, chosenMood]);
  const pickMood = (m: Mood) => {
    stopSound();
    setMood(m);
    const first = songs.find((t) => t.moodId === m);
    if (first) setPicked(first.id);
    else void compose(m);
  };
  const moodSongs = songs.filter((t) => t.moodId === chosenMood);

  function stopSound() {
    audioRef.current?.pause();
    const video = videoRef.current;
    if (video) {
      video.pause();
      // With no song playing the reel loops quietly on the page again.
      video.muted = true;
    }
    setPlaying(null);
  }
  function play(song: Song) {
    const audio = audioRef.current;
    const video = videoRef.current;
    setPicked(song.id);
    if (playing === song.id) {
      stopSound();
      tryPlay(video);
      return;
    }
    if (!audio) return;
    audio.src = song.url;
    audio.currentTime = 0;
    if (video) {
      video.currentTime = 0;
      // A tap auditions the song as it will render: the place's own sound under it.
      video.volume = ORIGINAL_UNDER_MUSIC;
      video.muted = false;
      tryPlay(video);
    }
    playSong(audio, song.id);
  }
  const pickedSong = songs.find((t) => t.id === picked) ?? null;
  const toggleSound = () => {
    if (playing) {
      stopSound();
      tryPlay(videoRef.current);
    } else if (pickedSong) play(pickedSong);
  };

  /* ------------------------------------------------- the actions */

  /** `then` is what the credits sheet says it will do once the pack lands: "build the storyboard". */
  const run = async (then: string, cost: number, action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      const short = creditShortfall(e);
      if (short) credits.open(short.needed, () => void run(then, cost, action), then, reelTotal);
      else setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const startJob = async (input: ReelRequest) => {
    const id = await save();
    const job = await startReelJob(id, input);
    qc.setQueryData<ReelJobDto[]>(['reel-jobs', id], (old) => [
      ...(old ?? []).filter((j) => j.id !== job.id),
      job,
    ]);
    setEditing(false);
    setRevision('');
    setSceneNote('');
    setSceneAction(null);
    setWholeBoard(false);
    if (input.kind !== 'storyboard' || !input.scenes) setSceneIdx(0);
    void qc.invalidateQueries({ queryKey: keys.me });
  };
  const paid = (cost: number, then: string, action: () => Promise<void>) => {
    if (balance !== null && balance < cost)
      credits.open(cost, () => void run(then, cost, action), then, reelTotal);
    else void run(then, cost, action);
  };
  const build = () =>
    paid(boardCost, 'build the storyboard', () =>
      startJob({ kind: 'storyboard', seconds, fidelity, revision: '' }),
    );
  const revise = () =>
    board &&
    paid(reelQuote('storyboard', board.input.seconds).credits, 'update the storyboard', () =>
      startJob({
        kind: 'storyboard',
        seconds: board.input.seconds,
        fidelity: board.input.fidelity,
        revision,
        parentId: board.id,
      }),
    );
  /** One scene drawn again from its own notes, or from a note about it: two credits, exactly. */
  const redoScene = (i: number) =>
    board &&
    paid(2, `draw scene ${i + 1} again`, () =>
      startJob({
        kind: 'storyboard',
        seconds: board.input.seconds,
        fidelity: board.input.fidelity,
        revision: sceneNote.trim(),
        parentId: board.id,
        scenes: [i + 1],
      }),
    );
  const film = () =>
    board &&
    paid(filmCost, 'film it', () =>
      startJob({
        kind: 'video',
        seconds: board.input.seconds,
        fidelity: board.input.fidelity,
        revision: '',
        approvedBoardId: board.id,
      }),
    );
  /** Rearranging is free: the scenes are re-listed, one swapped for a photo or one dropped. */
  const arrange = (scenes: { shotId?: string; mediaId?: string }[], showIdx: number) =>
    board &&
    void run('scenes', 0, async () => {
      const { job } = await api<{ job: ReelJobDto }>(
        `/api/posts/${postId}/reels/${board.id}/scenes`,
        { scenes },
      );
      qc.setQueryData<ReelJobDto[]>(['reel-jobs', postId], (old) => [
        ...(old ?? []).filter((j) => j.id !== job.id),
        job,
      ]);
      setSceneAction(null);
      setSceneIdx(showIdx);
    });
  const dropScene = (i: number) => {
    // Putting it back re-lists the board it was dropped from, under that board's own id.
    setUndo({
      boardId: board!.id,
      scene: i + 1,
      scenes: board!.result!.shots!.map((s) => ({ shotId: s.id })),
    });
    arrange(
      board!.result!.shots!.filter((_, k) => k !== i).map((s) => ({ shotId: s.id })),
      0,
    );
  };
  const putBack = () => {
    const u = undo;
    if (!u) return;
    setUndo(null);
    void run('scenes', 0, async () => {
      const { job } = await api<{ job: ReelJobDto }>(
        `/api/posts/${postId}/reels/${u.boardId}/scenes`,
        { scenes: u.scenes },
      );
      qc.setQueryData<ReelJobDto[]>(['reel-jobs', postId], (old) => [
        ...(old ?? []).filter((j) => j.id !== job.id),
        job,
      ]);
      setSceneIdx(u.scene - 1);
    });
  };
  const swapScene = (i: number, mediaId: string) =>
    arrange(
      board!.result!.shots!.map((s, k) => (k === i ? { mediaId } : { shotId: s.id })),
      i,
    );
  /** The one finishing render: the words laid on the reel, the song under it, or either alone. */
  const finish = (song: Song | null) => {
    if (!post || !version) return;
    stopSound();
    void run('music', 0, async () => {
      const video = videoRef.current;
      const durationMs = Math.floor((video?.duration || song?.seconds || 0) * 1000);
      const base = reelDraft(version);
      const endMs = durationMs > 0 ? durationMs : base.trim.endMs;
      // A line per scene bakes as timed overlays, each on for its scene; one line is one overlay.
      const timed = wordsLayer && lines ? wordsLayers(wordsLayer, lines) : null;
      // The lines follow the film's real length; until the browser has it, the planned seconds.
      const timings = timed
        ? sceneTimings(sceneShots, durationMs > 0 ? durationMs : plannedMs || endMs)
        : [];
      const layerTimings = timed
        ? timed.flatMap((l) => {
            const t = timings[Number(l.id.slice('words-'.length)) - 1];
            return t ? [{ layerId: l.id, startMs: t.startMs, endMs: t.endMs }] : [];
          })
        : [];
      const gen = await renderReel(post.id, active.id, version, {
        ...base,
        trim: { startMs: 0, endMs },
        layers: timed ?? (wordsLayer ? [wordsLayer] : []),
        ...(layerTimings.length ? { layerTimings } : {}),
        audio: song ? { kind: 'media', mediaId: song.id } : null,
        audioFile: undefined,
        // The reel carries the place's own sound; the song goes over it, not instead of it.
        ...(song ? { sound: defaultReelSound(true) } : {}),
      });
      setRevisit(null);
      followMix(gen.id, song ? 'music' : 'words');
    });
  };
  const useSong = () => {
    const song = songs.find((t) => t.id === picked);
    if (song) finish(song);
  };
  const keepQuiet = () => {
    stopSound();
    // Words with no song still need the render that bakes them in; nothing at all is done as is.
    if (wordsLayer) {
      finish(null);
      return;
    }
    if (postId) keepSilent(postId, true);
    saveChoices({ silent: true });
    setSilent(true);
    setChangingMusic(false);
    setRevisit(null);
  };
  const backToMusic = () => {
    if (postId) keepSilent(postId, false);
    saveChoices({ silent: false });
    setSilent(false);
    auditioned.current = null;
    setSongs([]);
    setPicked(null);
    setChangingMusic(true);
  };
  const saveCaption = async (body: string) => {
    if (!post) return;
    const parts = splitCaption(post.caption);
    if (body === parts.body) return;
    try {
      const p = await postsApi.update(post.id, { caption: joinCaption({ ...parts, body }) });
      qc.setQueryData(keys.post(post.id), p);
      setCaptionBody(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const saveHashtags = async (hashtags: string[]) => {
    if (!post) return;
    const parts = splitCaption(post.caption);
    if (hashtags.join() === parts.hashtags.join()) return;
    try {
      const p = await postsApi.update(post.id, { caption: joinCaption({ ...parts, hashtags }) });
      qc.setQueryData(keys.post(post.id), p);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const allowed = propertyChannels(active.plan);
  const connectedChannels = (connected.data?.connections ?? [])
    .filter((c) => c.status === 'connected')
    .map((c) => c.channel)
    .filter((c) => allowed.includes(c));
  const pickedChannels = (channels ?? post?.channels ?? active.channels).filter((c) =>
    connectedChannels.includes(c),
  );
  const toggleChannel = (c: Channel) =>
    setChannels(
      pickedChannels.includes(c) ? pickedChannels.filter((x) => x !== c) : [...pickedChannels, c],
    );
  const confirmSchedule = async (input: {
    date: string;
    time: string;
    publishNow: boolean;
    tiktok?: unknown;
  }) => {
    if (!post) return;
    setBusy(true);
    setError(null);
    try {
      const { post: scheduled, generation } = await postsApi.schedule(post.id, {
        ...(input as Parameters<typeof postsApi.schedule>[1]),
        channels: pickedChannels,
        timezone: active.timezone ?? hostTimeZone(),
      });
      qc.setQueryData(keys.post(scheduled.id), scheduled);
      let g = generation;
      while (g && g.status === 'running') {
        await new Promise((r) => window.setTimeout(r, 1500));
        g = await postsApi.poll(g.id);
      }
      if (g?.post) qc.setQueryData(keys.post(g.post.id), g.post);
      await qc.invalidateQueries({ queryKey: keys.posts(active.id) });
      void qc.invalidateQueries({ queryKey: keys.plan(active.id) });
      saveReelDraft(active.id, null);
      if (input.publishNow)
        void navigate('/app/posts', {
          state: { tab: 'Scheduled', toast: `Sharing now · ${pickedChannels.join(', ')}` },
        });
      else
        void navigate('/app/calendar', {
          state: {
            newPostId: scheduled.id,
            toast: `Scheduled · ${dayLabel(new Date(input.date + 'T00:00:00'))} · ${input.time}`,
          },
        });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const startOver = () => {
    stopSound();
    saveReelDraft(active.id, null);
    void navigate('/app/reel', { replace: true, state: { fresh: crypto.randomUUID() } });
  };
  const byHand = () => {
    saveWizard(active.id, {
      kind: 'Reel',
      path: 'own',
      line: brief,
      picks: [],
      shapeId: 'caption',
      input: {},
    });
    void navigate('/app/create?own=1');
  };

  /* ------------------------------------------------- what is on the stage */

  /**
   * A picture's address. A redo keeps the scenes it did not touch, so each file is asked for
   * through the first job that made it: an unchanged scene keeps its address and comes from the
   * browser's cache instead of flashing empty while it loads again.
   */
  const asset = (job: ReelJobDto, path: string) => {
    const file = path.split('/').pop();
    const first =
      jobs.find(
        (j) =>
          j.state === 'complete' &&
          [j.result?.url, ...(j.result?.shots?.map((s) => s.image) ?? [])].some(
            (p) => p?.split('/').pop() === file,
          ),
      ) ?? job;
    return `/api/posts/${postId}/reels/${first.id}/assets/${file}`;
  };
  const allPhotos = (library.data ?? []).filter(
    (m) => m.kind === 'photo' && !!m.url && m.source !== 'generated' && m.source !== 'render',
  );
  const photos = allPhotos.slice(0, 3);
  const shots = board?.result?.shots ?? [];
  const shown = shots[Math.min(sceneIdx, Math.max(0, shots.length - 1))] ?? null;
  const rail = railFor(phase);
  const at = rail.findIndex((r) => r.status === 'now') + 1;
  const kick = `Reel · step ${at} of ${rail.length}`;
  const failed = latest?.state === 'failed' && !working ? latest.error : null;
  const takenDates = new Set(
    (posts.data ?? [])
      .filter((p) => p.scheduledDate && p.status !== 'draft' && p.id !== post?.id)
      .map((p) => p.scheduledDate!),
  );
  const captionParts = post ? splitCaption(post.caption) : { body: '', link: null, hashtags: [] };

  const stage = (() => {
    if (phase === 'words' || phase === 'music' || phase === 'mixing' || phase === 'share') {
      const live = phase === 'words' || phase === 'music';
      return (
        <>
          <video
            ref={videoRef}
            onPlay={() => setReelPlaying(true)}
            onPause={() => setReelPlaying(false)}
            onEnded={() => setReelPlaying(false)}
            onLoadedMetadata={(e) => setVideoMs(Math.floor(e.currentTarget.duration * 1000) || 0)}
            onTimeUpdate={(e) => setPlayMs(Math.floor(e.currentTarget.currentTime * 1000))}
            // No browser controls over the host's words: a tap plays or pauses the finished reel.
            onClick={
              phase === 'share'
                ? (e) => {
                    const v = e.currentTarget;
                    if (v.paused) tryPlay(v);
                    else v.pause();
                  }
                : undefined
            }
            playsInline
            muted={live}
            autoPlay={live}
            loop={live}
            preload={live ? 'auto' : 'metadata'}
            // While the words are placed, the clean filmed source plays under them: a version
            // with earlier words baked in would show both. The finished reel plays itself.
            src={(live && version ? reelSource(version) : version?.videoUrl) || undefined}
            // Until a cover exists the first scene stands in: the phone is never a black box.
            poster={
              version?.coverUrl ?? (board && shots[0] ? asset(board, shots[0].image) : undefined)
            }
            aria-label="Your reel"
          />
          {phase === 'share' && !reelPlaying && (
            <button
              type="button"
              className="rf-sound off"
              onClick={() => tryPlay(videoRef.current)}
            >
              Play the reel
            </button>
          )}
          {phase === 'music' && pickedSong && (
            <button
              type="button"
              className={`rf-sound${playing ? '' : ' off'}${wordsLayer && wordsLayer.y < 0.45 ? ' low' : ''}`}
              aria-pressed={!!playing}
              onClick={toggleSound}
            >
              {playing ? 'Sound on' : 'Play the song'}
            </button>
          )}
          {live && wordsLayer && (
            <div
              className={`rf-words${phase === 'words' ? ' place' : ''}${drag ? ' drag' : ''}`}
              role={phase === 'words' ? 'button' : undefined}
              aria-label={phase === 'words' ? 'Drag the words to place them' : undefined}
              tabIndex={phase === 'words' ? 0 : undefined}
              style={{
                left: `${wordsLayer.x * 100}%`,
                top: `${wordsLayer.y * 100}%`,
                width: `${wordsLayer.w * 100}%`,
                fontSize: `${wordsLayer.size * 100}cqw`,
                fontFamily: PICTURE_FONTS[wordsLayer.font],
                fontWeight: wordsLayer.weight === 'bold' ? 700 : 400,
                textAlign: wordsLayer.align,
                color: wordsLayer.color,
                visibility: liveWords.trim() ? undefined : 'hidden',
              }}
              onPointerDown={dragStart}
              onPointerMove={dragMove}
              onPointerUp={dragEnd}
              onPointerCancel={dragEnd}
            >
              {wordsLayer.shade && <span className="rf-words-shade" aria-hidden />}
              <span
                className={`rf-words-text${wordsLayer.pill ? ' pill' : ''}`}
                style={wordsLayer.pill ? { background: wordsLayer.pill } : undefined}
              >
                {liveWords || ' '}
              </span>
            </div>
          )}
          {phase === 'mixing' && (
            <div className="rf-veil">
              {mixing?.what === 'words' ? 'Adding the words' : 'Adding the music'}
            </div>
          )}
        </>
      );
    }
    if (phase === 'filming' || (phase === 'board' && shown))
      return (
        <>
          {shown && board && <img src={asset(board, shown.image)} alt="" />}
          {phase === 'filming' ? (
            <div className="rf-veil">Filming</div>
          ) : (
            <span className="rf-stage-tag">
              Scene {sceneIdx + 1} · {shown!.title}
            </span>
          )}
        </>
      );
    if (phase === 'building') {
      // A redo keeps the board on the stage; only the scenes being drawn again shimmer.
      const redoing = working?.input.kind === 'storyboard' ? working.input.scenes : undefined;
      if (redoing && board && shots.length)
        return (
          <div className="rf-tiles" aria-hidden>
            {shots.map((s, i) =>
              redoing.includes(i + 1) ? (
                <span key={s.id} className="rf-tile">
                  <span className="shimmer" />
                </span>
              ) : (
                <img key={s.id} src={asset(board, s.image)} alt="" />
              ),
            )}
          </div>
        );
      return (
        <div className="rf-tiles" aria-hidden>
          {Array.from({ length: Math.max(6, working?.result?.shots?.length ?? 0) }, (_, i) => {
            const s = working?.result?.shots?.[i];
            return s?.image && working ? (
              <img key={i} src={asset(working, s.image)} alt="" />
            ) : (
              <span key={i} className="rf-tile">
                <span className="shimmer" />
              </span>
            );
          })}
        </div>
      );
    }
    return (
      <div className="rf-photos" aria-hidden>
        {photos.map((m) => (
          <img key={m.id} src={m.url!} alt="" />
        ))}
        <span className="rf-stage-tag">Your reel appears here</span>
      </div>
    );
  })();

  /* ------------------------------------------------- the one thing to do now */

  const steps = (list: FlowStep[]) => (
    <ol className="rf-steps" role="status" aria-label="Progress">
      {list.map((s) => (
        <li key={s.label} className={s.status}>
          <span className="m" aria-hidden>
            {s.status === 'done' ? '✓' : s.status === 'doing' ? '●' : ''}
          </span>
          {s.label}
        </li>
      ))}
    </ol>
  );
  const line = (cost: number) => creditLine(balance, cost);
  const primaryLabel = (label: string, cost: number) =>
    line(cost).short ? `Get credits · ${cost} needed` : `${label} · ${cost} credits`;

  const column = (() => {
    switch (phase) {
      case 'moment':
        return (
          <Moment
            kind="Reel"
            kinds={['Reel']}
            onKind={() => {}}
            line={brief}
            onLine={setBrief}
            placeholder="e.g. A house tour that ends on the hot tub at dusk"
            error={
              brief.length > BRIEF_MAX
                ? `Keep the idea under ${BRIEF_MAX.toLocaleString('en-US')} characters`
                : null
            }
            credits={balance === null ? null : { balance }}
            spend={0}
            seconds={seconds}
            onSeconds={setSeconds}
            fidelity={fidelity}
            onFidelity={setFidelity}
            shapeId="idea"
            onShape={() => {}}
            following={following}
            followingLine={
              following
                ? `Following @${following.handle}’s reel · its shape, your place`
                : undefined
            }
            onLetGo={() => {
              setFollowing(null);
              setTemplate(null);
            }}
            busy={locked}
            onGo={build}
            quiet={
              board
                ? { label: 'Keep the storyboard I have', onClick: () => setEditing(false) }
                : { label: 'Or post a video you filmed', onClick: byHand }
            }
          />
        );
      case 'building':
        return (
          <>
            <span className="rf-kick">{kick}</span>
            {working?.input.kind === 'storyboard' && working.input.scenes?.length ? (
              <>
                <h1>Drawing {sceneList(working.input.scenes)} again</h1>
                <p className="rf-lede">
                  About a minute. The other scenes stay as they are.
                  {working.input.revision ? ' Your note:' : ''}
                </p>
                {working.input.revision && (
                  <blockquote className="rf-brief">{working.input.revision}</blockquote>
                )}
              </>
            ) : (
              <>
                <h1>Building your storyboard</h1>
                <p className="rf-lede">
                  Two or three minutes. It keeps going if you leave this page; come back any time.
                </p>
                <span className="rf-small">Your idea, as we read it</span>
                <blockquote className="rf-brief">{post?.brief ?? brief}</blockquote>
              </>
            )}
            {steps(storyboardSteps(working))}
            <span className="rf-small">If this fails, your credits come back.</span>
          </>
        );
      case 'board':
        return (
          <>
            <span className="rf-kick">{kick}</span>
            <h1>Check the scenes</h1>
            <p className="rf-lede">
              {[board?.result?.story?.title, board?.result?.story?.summary?.split(/(?<=\.)\s/)[0]]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <ul className="rf-scenes" aria-label="Scenes">
              {shots.map((s, i) => (
                <li key={s.id} className={i === sceneIdx ? 'on' : ''}>
                  <button
                    type="button"
                    className="rf-scene"
                    aria-pressed={i === sceneIdx}
                    onClick={() => setSceneIdx(i)}
                  >
                    <img src={asset(board!, s.image)} alt={`Scene ${i + 1} · ${s.title}`} />
                    <span>{s.title}</span>
                  </button>
                </li>
              ))}
            </ul>
            {undo && (
              <span className="rf-small" role="status">
                Scene {undo.scene} removed ·{' '}
                <button type="button" className="nx-quiet" disabled={locked} onClick={putBack}>
                  Put it back
                </button>
              </span>
            )}
            {shown && (
              <div className="rf-scene-row" role="group" aria-label={`Scene ${sceneIdx + 1}`}>
                <span className="rf-scene-name">
                  Scene {sceneIdx + 1} · {shown.title}
                </span>
                <div className="rf-scene-acts">
                  <button
                    type="button"
                    className={`nx-chip${sceneAction === 'redo' ? ' on' : ''}`}
                    disabled={locked}
                    aria-expanded={sceneAction === 'redo'}
                    onClick={() => setSceneAction(sceneAction === 'redo' ? null : 'redo')}
                  >
                    Redo this scene · 2 credits
                  </button>
                  <button
                    type="button"
                    className={`nx-chip${sceneAction === 'swap' ? ' on' : ''}`}
                    disabled={locked}
                    aria-expanded={sceneAction === 'swap'}
                    onClick={() => setSceneAction(sceneAction === 'swap' ? null : 'swap')}
                  >
                    Use one of my photos · free
                  </button>
                  {shots.length > 2 && (
                    <button
                      type="button"
                      className="nx-chip"
                      disabled={locked}
                      onClick={() => dropScene(sceneIdx)}
                    >
                      Remove · free · the reel plays {shots.length - 1} scenes
                    </button>
                  )}
                </div>
                {sceneAction === 'redo' && (
                  <div className="rf-tweak">
                    <textarea
                      aria-label={`What should change in scene ${sceneIdx + 1}`}
                      value={sceneNote}
                      disabled={locked}
                      rows={2}
                      placeholder="Optional. Say what should be different, or leave it blank for a fresh take."
                      onChange={(e) => setSceneNote(e.target.value)}
                    />
                    <div className="rf-scene-acts">
                      <button
                        type="button"
                        className="btn btn-primary hv"
                        disabled={locked}
                        onClick={() => redoScene(sceneIdx)}
                      >
                        {busy ? 'Starting…' : primaryLabel(`Draw scene ${sceneIdx + 1} again`, 2)}
                      </button>
                      <span className="rf-small">
                        About a minute. Every new frame is checked before you see it.
                      </span>
                    </div>
                  </div>
                )}
                {sceneAction === 'swap' && (
                  <div className="rf-swap">
                    <span className="rf-small">Tap a photo to use it as scene {sceneIdx + 1}.</span>
                    <div className="rf-photo-grid" role="group" aria-label="Your photos">
                      {allPhotos.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          disabled={locked}
                          aria-label={`Use ${m.label || 'this photo'} for scene ${sceneIdx + 1}`}
                          title={m.label || undefined}
                          onClick={() => swapScene(sceneIdx, m.id)}
                        >
                          <img src={m.url!} alt="" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {wholeBoard && (
              <div className="rf-tweak">
                <textarea
                  aria-label="Change something"
                  value={revision}
                  disabled={locked}
                  rows={2}
                  placeholder="Open on the view. Make scene 3 about the kitchen."
                  onChange={(e) => setRevision(e.target.value)}
                />
                <div className="rf-scene-acts">
                  <button
                    type="button"
                    className="nx-chip ai"
                    disabled={locked || !revision.trim()}
                    onClick={revise}
                  >
                    Redo{' '}
                    {
                      revisionQuote(
                        revision,
                        shots.length,
                        reelQuote('storyboard', board!.input.seconds).credits,
                      ).label
                    }
                  </button>
                  <span className="rf-small">
                    2 credits a changed scene. Name the scenes for an exact price; whatever is not
                    used comes back.
                  </span>
                </div>
              </div>
            )}
            <div className="rf-actions">
              <button
                type="button"
                className="btn btn-primary btn-lg hv"
                disabled={locked || !!revision.trim()}
                onClick={film}
              >
                {busy ? 'Starting…' : primaryLabel('Film it', filmCost)}
              </button>
              <span className="rf-small">
                {revision.trim()
                  ? 'Redo the scenes first, or clear the note to film as is.'
                  : `${line(filmCost).text}${line(filmCost).text ? ' · ' : ''}${filmEstimate(sceneShots.length)}`}
              </span>
              {!wholeBoard && (
                <button
                  type="button"
                  className="nx-quiet"
                  disabled={locked}
                  onClick={() => setWholeBoard(true)}
                >
                  Redo the whole board with a note · up to {boardCost} credits
                </button>
              )}
              <button
                type="button"
                className="nx-quiet"
                disabled={locked}
                onClick={() => {
                  setSeconds(board!.input.seconds);
                  setFidelity(board!.input.fidelity);
                  setEditing(true);
                }}
              >
                Change the idea · rebuilds for {boardCost} credits
              </button>
            </div>
          </>
        );
      case 'filming':
        return (
          <>
            <span className="rf-kick">{kick}</span>
            <h1>Filming your reel</h1>
            <p className="rf-lede">
              {filmLede(
                filmLeft,
                sceneShots.length,
                filmJob?.createdAt ?? new Date(now).toISOString(),
                now,
              )}
            </p>
            {steps(filmSteps(filmJob, filmed, sceneShots.length))}
            <span className="rf-small">If this fails, your credits come back.</span>
          </>
        );
      case 'words':
        return (
          <>
            <span className="rf-kick">{kick}</span>
            <h1>Words on it</h1>
            <p className="rf-lede">
              {lines
                ? 'A line for each scene, changing with the picture. Drag them on the phone to place them. Free.'
                : 'A line that sits on the reel the whole way through. Drag it on the phone to place it. Free.'}
            </p>
            {wordsLayer ? (
              <>
                {sceneShots.length >= 2 && (
                  <div className="nx-seg small" role="radiogroup" aria-label="Words">
                    {[
                      { id: 'scenes', label: 'A line per scene' },
                      { id: 'one', label: 'One line throughout' },
                    ].map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        role="radio"
                        aria-checked={o.id === 'scenes' ? !!lines : !lines}
                        className={(o.id === 'scenes' ? !!lines : !lines) ? 'on' : ''}
                        onClick={() => {
                          if (o.id === 'scenes' && !wordsLayer.weight)
                            // Words from before this build carry no weight: lines per scene start bold.
                            setWordsLayer({
                              ...wordsLayer,
                              weight: 'bold',
                              pill: wordsLayer.pill ?? '#111111',
                              shade: false,
                            });
                          setLines(
                            o.id === 'scenes'
                              ? (lines ??
                                  sceneLines(
                                    sceneShots,
                                    titleWords(
                                      board?.result?.story?.title ?? post?.title,
                                      splitCaption(post?.caption ?? '').body,
                                    ),
                                  ))
                              : null,
                          );
                        }}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                )}
                {lines ? (
                  <ol className="rf-lines" aria-label="The words, scene by scene">
                    {sceneShots.map((shot, i) => (
                      <li key={shot.id}>
                        <span className="rf-small">
                          Scene {i + 1} · {shot.title}
                        </span>
                        <input
                          className="rf-words-input"
                          aria-label={`Words on scene ${i + 1}`}
                          value={lines[i] ?? ''}
                          maxLength={60}
                          onFocus={() => {
                            // Jump the phone to this scene, so the line is seen where it lands.
                            const v = videoRef.current;
                            const t = lineTimings[i];
                            if (v && t) {
                              v.currentTime = t.startMs / 1000 + 0.05;
                              setPlayMs(t.startMs + 50);
                              tryPlay(v);
                            }
                          }}
                          onChange={(e) =>
                            setLines(lines.map((l, k) => (k === i ? e.target.value : l)))
                          }
                        />
                      </li>
                    ))}
                  </ol>
                ) : (
                  <input
                    className="rf-words-input"
                    aria-label="The words"
                    value={wordsLayer.text}
                    maxLength={60}
                    onChange={(e) => setWordsLayer({ ...wordsLayer, text: e.target.value })}
                  />
                )}
                <div className="rf-row">
                  <div className="nx-seg small" role="radiogroup" aria-label="Font">
                    {(['sans', 'serif'] as const).map((f) => (
                      <button
                        key={f}
                        type="button"
                        role="radio"
                        aria-checked={wordsLayer.font === f}
                        className={wordsLayer.font === f ? 'on' : ''}
                        onClick={() => setWordsLayer({ ...wordsLayer, font: f })}
                      >
                        {f === 'sans' ? 'Sans' : 'Serif'}
                      </button>
                    ))}
                  </div>
                  <div className="nx-seg small" role="radiogroup" aria-label="Weight">
                    {(['bold', 'regular'] as const).map((w) => (
                      <button
                        key={w}
                        type="button"
                        role="radio"
                        aria-checked={(wordsLayer.weight ?? 'regular') === w}
                        className={(wordsLayer.weight ?? 'regular') === w ? 'on' : ''}
                        onClick={() => setWordsLayer({ ...wordsLayer, weight: w })}
                      >
                        {w === 'bold' ? 'Bold' : 'Regular'}
                      </button>
                    ))}
                  </div>
                  <div className="nx-seg small" role="radiogroup" aria-label="Size">
                    {WORD_SIZES.map((z) => (
                      <button
                        key={z.id}
                        type="button"
                        role="radio"
                        aria-checked={wordsLayer.size === z.size}
                        className={wordsLayer.size === z.size ? 'on' : ''}
                        onClick={() => setWordsLayer({ ...wordsLayer, size: z.size })}
                      >
                        {z.label}
                      </button>
                    ))}
                  </div>
                  <div className="nx-seg small" role="radiogroup" aria-label="Behind the words">
                    {[
                      { id: 'shade', label: 'Soft shade' },
                      { id: 'pill', label: 'Pill' },
                    ].map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        role="radio"
                        aria-checked={o.id === 'pill' ? !!wordsLayer.pill : !wordsLayer.pill}
                        className={
                          (o.id === 'pill' ? !!wordsLayer.pill : !wordsLayer.pill) ? 'on' : ''
                        }
                        onClick={() =>
                          setWordsLayer({
                            ...wordsLayer,
                            pill: o.id === 'pill' ? '#111111' : null,
                            shade: o.id !== 'pill',
                          })
                        }
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <button
                type="button"
                className="nx-chip"
                onClick={() =>
                  setWordsLayer(
                    defaultWords(
                      titleWords(
                        board?.result?.story?.title ?? post?.title,
                        splitCaption(post?.caption ?? '').body,
                      ),
                    ),
                  )
                }
              >
                Add words
              </button>
            )}
            <div className="rf-actions">
              <button
                type="button"
                className="btn btn-primary btn-lg hv"
                disabled={busy || !(lines ? lines.some((l) => l.trim()) : wordsLayer?.text.trim())}
                onClick={() => settleWords(wordsLayer)}
              >
                Keep these words ▸
              </button>
              <span className="rf-small">
                Baked into the reel when you finish it, with or without music.
              </span>
              <button
                type="button"
                className="nx-quiet"
                disabled={busy}
                onClick={() => settleWords(null)}
              >
                No words on it
              </button>
            </div>
          </>
        );
      case 'music':
        return (
          <>
            <span className="rf-kick">{kick}</span>
            <h1>Pick the music</h1>
            <p className="rf-lede">
              A song composed for this reel
              {playing
                ? ', playing over it now.'
                : '. Tap Play the song on the reel to hear it.'}{' '}
              Try another mood, or one more like this. Nothing you have heard is thrown away.
            </p>
            <div className="nx-chips rf-moods" role="radiogroup" aria-label="Mood">
              {MOODS.map((m) => {
                const n = songs.filter((t) => t.moodId === m.id).length;
                return (
                  <button
                    key={m.id}
                    type="button"
                    role="radio"
                    aria-checked={chosenMood === m.id}
                    className={`nx-chip${chosenMood === m.id ? ' on' : ''}`}
                    disabled={!!composing || busy}
                    onClick={() => pickMood(m.id)}
                  >
                    {m.label}
                    {n > 0 && chosenMood !== m.id ? ` · ${n}` : ''}
                  </button>
                );
              })}
            </div>
            <span className="rf-small">{MUSIC_PRICE_LINE}</span>
            <div className="rf-takes" role="radiogroup" aria-label="Songs">
              {moodSongs.map((song, i) => (
                <label key={song.id} className={`rf-take${picked === song.id ? ' on' : ''}`}>
                  <input
                    type="radio"
                    name="song"
                    aria-label={`${MOODS.find((m) => m.id === chosenMood)?.label} ${SONG_LETTERS[i] ?? i + 1} · ${song.title}`}
                    checked={picked === song.id}
                    onChange={() => setPicked(song.id)}
                  />
                  <button
                    type="button"
                    className="rf-play"
                    aria-label={`${playing === song.id ? 'Pause' : 'Play'} ${MOODS.find((m) => m.id === chosenMood)?.label} ${SONG_LETTERS[i] ?? i + 1}`}
                    onClick={() => play(song)}
                  >
                    {playing === song.id ? '❚❚' : '▶'}
                  </button>
                  <span className="rf-take-name">
                    {song.title.charAt(0).toUpperCase() + song.title.slice(1)}
                    <small>
                      {MOODS.find((m) => m.id === chosenMood)?.label} {SONG_LETTERS[i] ?? i + 1}
                      {song.seconds
                        ? ` · 0:${String(Math.round(song.seconds)).padStart(2, '0')}`
                        : ''}
                    </small>
                  </span>
                  {playing === song.id && (
                    <span className="rf-wave live" aria-hidden>
                      <i />
                      <i />
                      <i />
                      <i />
                      <i />
                    </span>
                  )}
                </label>
              ))}
              {composing && (
                <div className="rf-take waiting" role="status">
                  <span className="rf-play">
                    <span className="spinner" />
                  </span>
                  <span className="rf-take-name">
                    {MOODS.find((m) => m.id === composing)?.label}{' '}
                    {SONG_LETTERS[moodSongs.length] ?? moodSongs.length + 1}
                    <small>Composing · about fifteen seconds</small>
                  </span>
                </div>
              )}
            </div>
            {!composing && moodSongs.length > 0 && moodSongs.length < SONG_LETTERS.length && (
              <button
                type="button"
                className="nx-quiet"
                disabled={busy}
                onClick={() => void compose(chosenMood)}
              >
                One more like this · {MUSIC_PRICE}
              </button>
            )}
            <div className="rf-actions">
              <button
                type="button"
                className="btn btn-primary btn-lg hv"
                disabled={!picked || busy || !!composing}
                onClick={useSong}
              >
                Finish with this music
              </button>
              <span className="rf-small">
                Adding it to the reel is free
                {wordsLayer ? ' · your words go on with it' : ''} · about a minute, then it is ready
                to share
              </span>
              <button type="button" className="nx-quiet" disabled={busy} onClick={keepQuiet}>
                {wordsLayer ? 'Finish with the words and no music' : 'Finish with no music'}
              </button>
              <button type="button" className="nx-quiet" disabled={busy} onClick={reopenWords}>
                {wordsLayer ? 'Change the words' : 'Add words'}
              </button>
            </div>
            <audio ref={audioRef} preload="none" onEnded={() => setPlaying(null)} />
          </>
        );
      case 'mixing':
        return (
          <>
            <span className="rf-kick">{kick}</span>
            <h1>{mixing?.what === 'words' ? 'Adding the words' : 'Adding the music'}</h1>
            <p className="rf-lede">Under a minute. Your reel without it stays saved too.</p>
            {steps([
              {
                label:
                  mixing?.what === 'words'
                    ? 'Laying the words on the reel'
                    : wordsLayer
                      ? 'Laying the words and the track on the reel'
                      : 'Laying the track under the reel',
                status: 'doing',
              },
              { label: 'Saving the new version', status: 'todo' },
            ])}
          </>
        );
      case 'share':
        return (
          <>
            <span className="rf-kick">{kick}</span>
            <h1>{post?.status === 'failed' ? 'It did not go out' : 'Ready to share'}</h1>
            {post?.status === 'failed' && (
              <p className="rf-failed" role="alert">
                {publishFailure(post.publishError, post.channels)}{' '}
                <Link to="/app/channels">Open Channels</Link>
              </p>
            )}
            <p className="rf-lede">
              The caption is written from your storyboard. Change the words; the tags follow the
              caption.
            </p>
            <textarea
              aria-label="Caption"
              value={captionBody ?? captionParts.body}
              rows={Math.min(
                12,
                Math.max(4, Math.ceil((captionBody ?? captionParts.body).length / 60) + 1),
              )}
              onChange={(e) => setCaptionBody(e.target.value)}
              onBlur={(e) => void saveCaption(e.target.value)}
            />
            {captionParts.hashtags.length > 0 && (
              <p className="rf-tags">
                {captionParts.hashtags.map((t) => (
                  <span key={t}>#{t}</span>
                ))}
              </p>
            )}
            <div className="rf-channels" role="group" aria-label="Channels">
              {connectedChannels.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`nx-chip${pickedChannels.includes(c) ? ' on' : ''}`}
                  aria-pressed={pickedChannels.includes(c)}
                  onClick={() => toggleChannel(c)}
                >
                  {CHANNEL_META[c].label}
                </button>
              ))}
              {!connectedChannels.length && (
                <span className="rf-small">
                  No channel is connected yet. Connect{' '}
                  {allowed
                    .map((c) => CHANNEL_META[c].label)
                    .join(', ')
                    .replace(/, ([^,]*)$/, ' or $1')}{' '}
                  and this reel is ready to go; it stays saved in Drafts meanwhile.
                </span>
              )}
            </div>
            <div className="rf-actions">
              {connectedChannels.length ? (
                <button
                  type="button"
                  className="btn btn-primary btn-lg hv"
                  disabled={busy || post?.status === 'published' || !pickedChannels.length}
                  onClick={() => setSheet('share')}
                >
                  {post?.status === 'scheduled'
                    ? 'Reschedule ▸'
                    : post?.status === 'failed'
                      ? 'Post it again ▸'
                      : 'Schedule ▸'}
                </button>
              ) : (
                <Link to="/app/channels" className="btn btn-primary btn-lg hv">
                  Connect a channel ▸
                </Link>
              )}
              <a
                className="nx-quiet"
                href={`/api/posts/${post?.id}/versions/${version?.id}/download`}
                download
              >
                Download
              </a>
              <button type="button" className="nx-quiet" disabled={busy} onClick={backToMusic}>
                Change the music
              </button>
              <button type="button" className="nx-quiet" disabled={busy} onClick={reopenWords}>
                Change the words
              </button>
            </div>
          </>
        );
    }
  })();

  return (
    <div className="rf" data-phase={phase}>
      <FlowHeader
        place={active.name}
        title="Reel"
        newLabel="New reel"
        onNew={startOver}
        newDisabled={locked}
        saved={!!post}
        rail={
          <ol className="nx-rail" aria-label="Reel steps">
            {rail.map((r, i) => (
              <li key={r.label} className={r.status}>
                {r.status === 'done' && i === 0 && !working ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (board) {
                        setSeconds(board.input.seconds);
                        setFidelity(board.input.fidelity);
                      }
                      setEditing(true);
                    }}
                  >
                    <span className="n">✓</span>
                    <span>{r.label}</span>
                  </button>
                ) : (
                  <>
                    <span className="n">{r.status === 'done' ? '✓' : i + 1}</span>
                    <span>{r.label}</span>
                  </>
                )}
              </li>
            ))}
          </ol>
        }
      />
      <div className="rf-body">
        <div className="rf-stage">
          <div className="rf-phone" ref={phoneRef}>
            {stage}
          </div>
        </div>
        <section className="rf-side" aria-live="polite">
          {column}
          {(error || failed) && (
            <p role="alert" className="rf-error">
              {error || failed}
            </p>
          )}
        </section>
      </div>
      {sheet === 'share' && post && (
        <ScheduleSheet
          post={post}
          version={version}
          format={post.format}
          plan={active.plan}
          channels={pickedChannels}
          toggleChannel={toggleChannel}
          takenDates={takenDates}
          bestTime={active.bestTime}
          timezone={active.timezone ?? undefined}
          initialDate={post.scheduledDate ?? toIsoDate(new Date())}
          busy={busy}
          error={error}
          onClose={() => setSheet(null)}
          onCaption={(next) => void saveCaption(next)}
          onHashtags={(next) => void saveHashtags(next)}
          onConfirm={(input) => void confirmSchedule(input)}
        />
      )}
      {credits.element}
    </div>
  );
}
